/// <amd-module name="Pipelines/Jira/Service/JiraConfigurationService" />

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssService, IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { issueRequest } from "VSS/Platform/Fetch";
import { IVssContributionService, IServerException } from "VSS/Platform/Contribution";
import * as Utils_Url from "VSS/Platform/Util/Url";

import { IJiraConfigurationData, IJiraQueryParameters, IInitializeConfigurations } from "Pipelines/Jira/Common/Types";

/**
 * Service for jira configuration data
 */
export interface IJiraConfigurationService extends IVssService {
    /**
     * Gets the configurations with observable values that UI can subscribe to
     */
    initializeConfigurations(pageContext: IVssPageContext): IInitializeConfigurations;

    deleteAccountConfiguration(configuration: IJiraConfigurationData, params: IJiraQueryParameters, tfsDeploymentUrl: string): void;
}

export class JiraConfigurationService extends VssService implements IJiraConfigurationService {
    private _configurations: ObservableArray<IJiraConfigurationData>;
    private _deleteErrorMessage: ObservableValue<string>;

    public initializeConfigurations(pageContext: IVssPageContext): IInitializeConfigurations {
        let exceptionMessage: string = "";
        this._deleteErrorMessage = new ObservableValue<string>("");

        if (!this._configurations) {
            const dataProviderId = "ms.vss-pipelines-jira-web.jira-configuration-data-provider";
            const vssContributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

            const configurationsData: IJiraConfigurationData[] = vssContributionService.getData(dataProviderId) || [];
            this._configurations = new ObservableArray<IJiraConfigurationData>(configurationsData);
            
            const exception: IServerException = vssContributionService.getDataProviderExceptions()[dataProviderId];
            exceptionMessage = !!exception && exception.message ? exception.message : "";
        }

        return { 
            configurations: this._configurations, 
            deleteErrorMessage: this._deleteErrorMessage,
            initializeDataErrorMessage: exceptionMessage
        };
    }

    public deleteAccountConfiguration(configuration: IJiraConfigurationData, params: IJiraQueryParameters, tfsDeploymentUrl: string): void {
        let options: RequestInit = {
            body: JSON.stringify({
                "hostId": configuration.hostId,
                "baseUrl": params.xdm_e,
                "eventType": this._deleteAccountEventType
            }),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Authorization": "JWT " + params.jwt
            },
            method: "POST"
        };
        
        issueRequest(Utils_Url.combineUrlPaths(tfsDeploymentUrl, format("/_apis/public/Pipelines/Events?provider={0}&api-version=5.0-preview.1", this._jiraProviderId)), options).then((response: Response) => {
            (this._configurations.value || []).forEach((config: IJiraConfigurationData, index: number) => {
                if (config.hostId === configuration.hostId) {
                    this._configurations.splice(index, 1);
                }
            });
            this._deleteErrorMessage.value = "";
        }, (error: any) => {
            this._deleteErrorMessage.value = error.message;
        });
    }

    private readonly _deleteAccountEventType = "deleteaccount";
    private readonly _jiraProviderId = "jiraconnectapp";
}

Services.add("IJiraConfigurationService", { serviceFactory: JiraConfigurationService });
